/**
 * @file ColorDialogBody.tsx
 * @description Одно тело окна «Свой цвет» (phase19.16, партия SB120 —
 * свобода выбора; uiux-sb120-fields §3) для поля цвета `ColorField`
 * (текст и заливка) и окна цвета роли вкладки «Цвета» (`ColorPickerDialog`):
 * поле оттенка (react-colorful), код цвета, пипетка (если браузер умеет),
 * «Цвета вашего сайта», «Последние» (до 8, на устройстве, по магазину),
 * образец «Аа Пример текста» с контрастом и слот `extra` (образец роли
 * `ColorSample`). Цвет применяется по «Готово» — тогда же он попадает в
 * «Последние». Монтируется при открытии окна: начальный цвет без эффектов,
 * сохранение черновика в это время выбор не сбрасывает.
 */

import { SITE_COLOR_PATTERN, SITE_COLOR_ROLE_INFO, SITE_COLOR_ROLES, type SiteColors } from "@chaiberi/shared";
import { Pipette } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import { HexColorInput, HexColorPicker } from "react-colorful";

import { Button } from "../ui/button";
import { DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { ColorDialogSample } from "./ColorDialogSample";
import { ColorDotRow } from "./ColorDotRow";
import { useEyeDropper } from "../../hooks/site-fields/useEyeDropper";
import { useRecentColors } from "../../hooks/site-fields/useRecentColors";

export interface ColorDialogBodyProps {
  /** Цвет при открытии (`#rrggbb[aa]`; прозрачность отбрасывается). */
  initial: string;
  colors: SiteColors;
  /** text — образец цветом текста на фоне; fill — текст по тону на заливке. */
  mode: "text" | "fill";
  /** Фон под текстом образца в режиме text; нет — фон страницы. */
  sampleBg?: string;
  /** Образец «Аа Пример текста» (вкладка «Цвета» показывает свой — в `extra`). */
  sample?: boolean;
  /** Слот под образцом: текущий цвет и как его поменять. */
  extra?: (value: string, setValue: (hex: string) => void) => ReactNode;
  onCancel: () => void;
  onDone: (hex: string) => void;
}

export function ColorDialogBody({ initial, colors, mode, sampleBg, sample = true, extra, onCancel, onDone }: ColorDialogBodyProps) {
  const [value, setValue] = useState(initial.slice(0, 7).toLowerCase());
  const dropper = useEyeDropper();
  const { recent, remember } = useRecentColors();
  const hexId = useId();
  const valid = SITE_COLOR_PATTERN.test(value);

  async function pickFromScreen() {
    const hex = await dropper.pick();
    if (hex) setValue(hex);
  }

  function done() {
    remember(value);
    onDone(value);
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Размер — стилем элемента: CSS react-colorful вне слоёв Tailwind и перебивает классы. */}
        <HexColorPicker color={valid ? value : "#000000"} onChange={setValue} aria-label="Насыщенность, яркость и оттенок" style={{ width: "100%", height: "12rem" }} />
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor={hexId}>Код цвета</Label>
            <HexColorInput
              id={hexId}
              prefixed
              color={value}
              onChange={(next) => setValue(next.toLowerCase())}
              aria-invalid={!valid}
              className="h-12 w-40 rounded-xl border border-on-surface/15 bg-surface-lowest px-3 font-mono text-base outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          {dropper.supported ? (
            <Button variant="outline" className="h-12" onClick={() => void pickFromScreen()}>
              <Pipette aria-hidden />
              Пипетка
            </Button>
          ) : null}
        </div>
        <ColorDotRow
          title="Цвета вашего сайта"
          dots={SITE_COLOR_ROLES.map((r) => ({ hex: colors[r], label: `Взять цвет «${SITE_COLOR_ROLE_INFO[r].label}» ${colors[r]}` }))}
          onPick={setValue}
        />
        {recent.length > 0 ? <ColorDotRow title="Последние" dots={recent.map((hex) => ({ hex, label: `Взять последний цвет ${hex}` }))} onPick={setValue} /> : null}
        {sample ? <ColorDialogSample mode={mode} value={value} colors={colors} bg={sampleBg} /> : null}
        {extra?.(value, setValue)}
      </div>
      {/* На телефоне окно во весь экран и прокручивается — кнопки закреплены у нижнего края (отрицательные поля гасят p-6 окна). */}
      <DialogFooter className="max-sm:sticky max-sm:-bottom-6 max-sm:z-10 max-sm:-mx-6 max-sm:-mb-6 max-sm:bg-surface-lowest max-sm:px-6 max-sm:py-3 max-sm:[box-shadow:0_-1px_0_var(--outline-variant)]">
        <Button variant="ghost" onClick={onCancel}>
          Отменить
        </Button>
        <Button disabled={!valid} onClick={done}>
          Готово
        </Button>
      </DialogFooter>
    </>
  );
}
